import semver from 'semver';
import { GITHUB_API_BASE_URL } from './links';

const OWNER = 'lvgl';
const REPO = 'lvgl_editor';

export type GitHubReleaseAsset = {
    name: string;
    browser_download_url: string;
    size: number;
    content_type: string;
};

export type GitHubRelease = {
    id: number;
    tag_name: string;
    name: string | null;
    html_url: string;
    body: string | null;
    draft: boolean;
    prerelease: boolean;
    published_at: string | null;
    assets: GitHubReleaseAsset[];
};

/**
 * Fetch all releases for a GitHub repo and return the one with the highest
 * semver version parsed from its `tag_name`, which may not be the most
 * recently published release.
 */
export const getLatestEditorRelease = async (): Promise<GitHubRelease | null> => {
    try {
        const response: Response = await fetch(`${GITHUB_API_BASE_URL}/repos/${OWNER}/${REPO}/releases?per_page=100`);

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('GitHub API rate limit exceeded. Please try again later.');
            }
            throw new Error(`Failed to fetch releases: ${response.status} ${response.statusText}`);
        }

        const releases: GitHubRelease[] = await response.json();

        let latest: GitHubRelease | null = null;
        let latestVersion: semver.SemVer | null = null;

        for (const release of releases) {
            if (release.draft || release.prerelease) continue; // Skip draft and prerelease versions

            const version: semver.SemVer | null = semver.coerce(release.tag_name);
            if (!version) continue; // Skip tags that aren't parseable as a version
            if (!latestVersion || semver.gt(version, latestVersion)) {
                latest = release;
                latestVersion = version;
            }
        }

        return latest;
    } catch (error: unknown) {
        console.error(`Error fetching latest release for ${OWNER}/${REPO}: ${String(error)}`);
        throw error;
    }
};

/**
 * Compare a release's `tag_name` against a running version string and report
 * whether the release is a newer semver version.
 */
export const isNewerEditorRelease = (release: GitHubRelease | null, currentVersion: string): boolean => {
    if (!release) return false;

    // Use `parse` rather than `coerce` so prerelease tags (e.g. `1.2.0-beta`) are
    // preserved, since semver considers a stable version newer than a prerelease.
    const latestVersion: semver.SemVer | null = semver.parse(release.tag_name, { loose: true });
    const runningVersion: semver.SemVer | null = semver.parse(currentVersion, { loose: true });

    return !!(latestVersion && runningVersion && semver.gt(latestVersion, runningVersion));
};
